import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

export const KYC_IDS = {
  IDENTITY: 'identity',
  IDENTITY_INFORMATIONS: 'identityInformations',
} as const

export const ID_DOCUMENTS = {
  ID_CARD: 'id_verification_id_card',
  PASSPORT: 'id_verification_passeport',
  DRIVING_LICENCE: 'id_verification_driving_licence',
} as const

export const ID_INFORMATIONS = {
  SSN: 'ssn',
  DRIVING_LICENCE: 'driving_license',
} as const

export enum TradeInProviders {
  BM = 'bm',
  APAC = 'apac',
}

export type KycType = (typeof KYC_IDS)[keyof typeof KYC_IDS]
export type IdDocumentsType = (typeof ID_DOCUMENTS)[keyof typeof ID_DOCUMENTS]
export type IdInformationsType =
  (typeof ID_INFORMATIONS)[keyof typeof ID_INFORMATIONS]

export interface KYC {
  firstName: boolean
  lastName: boolean
  birthDate: boolean
  phone: boolean
  address: Record<string, boolean>
  postalCodeLength: {
    min: number
    max: number
  }
  [KYC_IDS.IDENTITY]: {
    list: Array<{
      type: IdDocumentsType
      isOneSide: boolean
    }>
  }
  [KYC_IDS.IDENTITY_INFORMATIONS]?: {
    required: boolean
    list: Array<IdInformationsType>
  }
}
export interface BuybackData {
  iban: boolean
  bacs: boolean
  recyclingFaq: Record<
    'backup' | 'counterOffer' | 'dropOff' | 'google' | 'icloud' | 'laPoste',
    string
  > | null
  tradeinFaq: Record<
    'backup' | 'counterOffer' | 'google' | 'icloud',
    string
  > | null
  csvFaq: string
  kyc: KYC
  hasGdprLegalMentions: boolean
  hasSmsMentions: boolean
  hasBankStep: boolean
  hasTinRequirement: boolean
  showRibCard: boolean
  showSwapSummaryMoreInfo: boolean
  /**
   * Whether the after-trade-in price is the emphasized (main) price on product cards.
   * When false (e.g. JP), the product price is highlighted and the after-trade-in
   * price is displayed as a secondary line below it.
   */
  emphasisOnAfterTradeInPrice: boolean
  /**
   * Whether the trade-in discount on product cards is only eligible when the
   * product has a new price to compare against. Markets without a new price
   * on their index (e.g. JP) set this to false.
   */
  requiresNewPriceForTradeInDiscount: boolean
  tradeInProvider: TradeInProviders | null
}

const buybackConfig: Partial<Record<MarketCountryCode, BuybackData>> = {
  [MarketCountryCode.FR]: {
    iban: true,
    bacs: false,
    hasBankStep: true,
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 5,
        max: 5,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.PASSPORT,
            isOneSide: true,
          },
          {
            type: ID_DOCUMENTS.ID_CARD,
            isOneSide: false,
          },
        ],
      },
    },
    recyclingFaq: {
      backup:
        'https://help.backmarket.com/hc/fr-fr/articles/12761011194524-Tout-ce-que-vous-devez-savoir-sur-les-comptes-utilisateurs',
      counterOffer:
        'https://help.backmarket.com/hc/fr-fr/articles/360021663319',
      dropOff: 'https://localiser.laposte.fr/?jesuis=particulier&contact=vente',
      google:
        'https://help.backmarket.com/hc/fr-fr/articles/12761011194524-Tout-ce-que-vous-devez-savoir-sur-les-comptes-utilisateurs#h_01HQ8CHVVX2XZGY60SSXER1VVF',
      icloud:
        'https://help.backmarket.com/hc/fr-fr/articles/12761011194524-Tout-ce-que-vous-devez-savoir-sur-les-comptes-utilisateurs#h_01HQ8E9RC7XFGP96ZSFDZXG7XT',
      laPoste: 'https://localiser.laposte.fr/?jesuis=particulier&contact=boite',
    },
    tradeinFaq: {
      backup:
        'https://help.backmarket.com/hc/fr-fr/articles/360010648979-Comment-sauvegarder-et-supp%5B%E2%80%A6%5D-il-aux-donn%C3%A9es-de-mon-appareil-apr%C3%A8s-sa-revente-',
      icloud:
        'https://help.backmarket.com/hc/fr-fr/articles/360012127479-Comment-d%C3%A9connecter-mon-compte-iCloud-Google-',
      google:
        'https://help.backmarket.com/hc/fr-fr/articles/360012127479-Comment-d%C3%A9connecter-mon-compte-iCloud-Google-',
      counterOffer:
        'https://help.backmarket.com/hc/fr-fr/articles/360021663319',
    },
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Bien-débuter-avec-vos-Annonces',
    hasGdprLegalMentions: true,
    hasSmsMentions: false,
    hasTinRequirement: true,
    showRibCard: true,
    showSwapSummaryMoreInfo: true,
    emphasisOnAfterTradeInPrice: true,
    requiresNewPriceForTradeInDiscount: true,
    tradeInProvider: TradeInProviders.BM,
  },
  [MarketCountryCode.US]: {
    iban: false,
    bacs: false,
    hasBankStep: false,
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        stateOrProvince: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 5,
        max: 5,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.ID_CARD,
            isOneSide: false,
          },
          {
            type: ID_DOCUMENTS.DRIVING_LICENCE,
            isOneSide: false,
          },
        ],
      },
      identityInformations: {
        required: true,
        list: ['driving_license', 'ssn'],
      },
    },
    recyclingFaq: {
      backup:
        'https://help.backmarket.com/hc/en-us/articles/12761011194524-Everything-you-need-to-know-about-user-accounts',
      counterOffer:
        'https://help.backmarket.com/hc/fr-fr/articles/360021663319',
      dropOff: 'https://www.ups.com/dropoff/?loc=en_US',
      google:
        'https://help.backmarket.com/hc/en-us/articles/12761011194524-Everything-you-need-to-know-about-user-accounts#h_01HQ8CHVVX2XZGY60SSXER1VVF',
      icloud:
        'https://help.backmarket.com/hc/en-us/articles/12761011194524-Everything-you-need-to-know-about-user-accounts#h_01HQ8E9RC7XFGP96ZSFDZXG7XT',
      laPoste: '',
    },
    tradeinFaq: {
      backup:
        'https://help.backmarket.com/hc/en-us/articles/360010648979-What-happens-to-the-data-on-my-device-after-I-trade-it-in-',
      icloud:
        'https://help.backmarket.com/hc/en-us/articles/360012127479-How-do-I-disconnect-my-iCloud-or-Google-account-',
      google:
        'https://help.backmarket.com/hc/en-us/articles/360012127479-How-do-I-disconnect-my-iCloud-or-Google-account-',
      counterOffer:
        'https://help.backmarket.com/hc/en-us/articles/360010965060',
    },
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
    hasGdprLegalMentions: false,
    hasSmsMentions: true,
    hasTinRequirement: true,
    showRibCard: true,
    showSwapSummaryMoreInfo: true,
    emphasisOnAfterTradeInPrice: true,
    requiresNewPriceForTradeInDiscount: true,
    tradeInProvider: TradeInProviders.BM,
  },
  [MarketCountryCode.DE]: {
    iban: true,
    bacs: false,
    hasBankStep: true,
    recyclingFaq: null,
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 5,
        max: 5,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.PASSPORT,
            isOneSide: true,
          },
          {
            type: ID_DOCUMENTS.ID_CARD,
            isOneSide: false,
          },
        ],
      },
    },
    tradeinFaq: {
      backup:
        'https://help.backmarket.com/hc/de/articles/360010648979-Was-passiert-mit-den-Daten-auf-meinem-Ger%C3%A4t-nachdem-ich-es-verkauft-habe-',
      icloud:
        'https://help.backmarket.com/hc/de/articles/360012127479-Wie-melde-ich-mich-von-meinem-iCloud-Konto-ab-',
      google:
        'https://help.backmarket.com/hc/de/articles/360012127479-Wie-melde-ich-mich-von-meinem-Google-iCloud-Konto-ab-',
      counterOffer: 'https://help.backmarket.com/hc/de/articles/360010965060',
    },
    hasGdprLegalMentions: true,
    hasSmsMentions: false,
    hasTinRequirement: true,
    showRibCard: true,
    showSwapSummaryMoreInfo: true,
    emphasisOnAfterTradeInPrice: true,
    requiresNewPriceForTradeInDiscount: true,
    tradeInProvider: TradeInProviders.BM,
  },
  [MarketCountryCode.ES]: {
    iban: true,
    bacs: false,
    hasBankStep: true,
    recyclingFaq: null,
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 5,
        max: 5,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.PASSPORT,
            isOneSide: true,
          },
          {
            type: ID_DOCUMENTS.ID_CARD,
            isOneSide: false,
          },
        ],
      },
    },
    tradeinFaq: {
      backup: 'https://help.backmarket.com/hc/es/articles/360010648979',
      icloud: 'https://help.backmarket.com/hc/es/articles/360012127479',
      google: 'https://help.backmarket.com/hc/es/articles/360012127479',
      counterOffer: 'https://help.backmarket.com/hc/es/articles/360021663319',
    },
    hasGdprLegalMentions: true,
    hasSmsMentions: false,
    hasTinRequirement: true,
    showRibCard: true,
    showSwapSummaryMoreInfo: true,
    emphasisOnAfterTradeInPrice: true,
    requiresNewPriceForTradeInDiscount: true,
    tradeInProvider: TradeInProviders.BM,
  },
  [MarketCountryCode.GB]: {
    iban: false,
    bacs: true,
    hasBankStep: true,
    recyclingFaq: null,
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 6,
        max: 8,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.PASSPORT,
            isOneSide: true,
          },
          {
            type: ID_DOCUMENTS.DRIVING_LICENCE,
            isOneSide: false,
          },
        ],
      },
    },
    tradeinFaq: {
      backup: 'https://help.backmarket.com/hc/en-gb/articles/360010648979',
      icloud: 'https://help.backmarket.com/hc/en-gb/articles/360012127479',
      google: 'https://help.backmarket.com/hc/en-gb/articles/360012127479',
      counterOffer:
        'https://help.backmarket.com/hc/fr-fr/articles/360021663319',
    },
    hasGdprLegalMentions: true,
    hasSmsMentions: false,
    hasTinRequirement: false,
    showRibCard: true,
    showSwapSummaryMoreInfo: true,
    emphasisOnAfterTradeInPrice: true,
    requiresNewPriceForTradeInDiscount: true,
    tradeInProvider: TradeInProviders.BM,
  },
  [MarketCountryCode.IT]: {
    iban: true,
    bacs: false,
    hasBankStep: true,
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 5,
        max: 5,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.PASSPORT,
            isOneSide: true,
          },
          {
            type: ID_DOCUMENTS.ID_CARD,
            isOneSide: false,
          },
        ],
      },
    },
    recyclingFaq: null,
    tradeinFaq: {
      backup: 'https://help.backmarket.com/hc/it-it/articles/360010648979',
      icloud: 'https://help.backmarket.com/hc/it-it/articles/360012127479',
      google: 'https://help.backmarket.com/hc/it-it/articles/360012127479',
      counterOffer:
        'https://help.backmarket.com/hc/it-it/articles/360021663319',
    },
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
    hasGdprLegalMentions: true,
    hasSmsMentions: false,
    hasTinRequirement: true,
    showRibCard: true,
    showSwapSummaryMoreInfo: true,
    emphasisOnAfterTradeInPrice: true,
    requiresNewPriceForTradeInDiscount: true,
    tradeInProvider: TradeInProviders.BM,
  },
  [MarketCountryCode.JP]: {
    iban: false,
    bacs: false,
    hasBankStep: false,
    recyclingFaq: null,
    tradeinFaq: null,
    csvFaq:
      'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
    kyc: {
      firstName: true,
      lastName: true,
      birthDate: true,
      phone: true,
      address: {
        street: true,
        street2: true,
        city: true,
        postalCode: true,
        country: true,
      },
      postalCodeLength: {
        min: 4,
        max: 10,
      },
      identity: {
        list: [
          {
            type: ID_DOCUMENTS.PASSPORT,
            isOneSide: true,
          },
          {
            type: ID_DOCUMENTS.ID_CARD,
            isOneSide: false,
          },
        ],
      },
    },
    hasGdprLegalMentions: false,
    hasSmsMentions: false,
    hasTinRequirement: false,
    showRibCard: false,
    showSwapSummaryMoreInfo: false,
    emphasisOnAfterTradeInPrice: false,
    requiresNewPriceForTradeInDiscount: false,
    tradeInProvider: TradeInProviders.APAC,
  },
}

// Conservative defaults for markets without a dedicated config: no market specific feature is enabled.
const fallbackBuybackConfig: BuybackData = {
  iban: false,
  bacs: false,
  hasBankStep: false,
  recyclingFaq: null,
  tradeinFaq: null,
  csvFaq:
    'https://backmarket.my.site.com/ssc/s/article/Getting-started-with-your-listings',
  kyc: {
    firstName: true,
    lastName: true,
    birthDate: true,
    phone: true,
    address: {
      street: true,
      street2: true,
      city: true,
      postalCode: true,
      country: true,
    },
    postalCodeLength: {
      min: 4,
      max: 10,
    },
    identity: {
      list: [
        {
          type: ID_DOCUMENTS.PASSPORT,
          isOneSide: true,
        },
        {
          type: ID_DOCUMENTS.ID_CARD,
          isOneSide: false,
        },
      ],
    },
  },
  hasGdprLegalMentions: false,
  hasSmsMentions: false,
  hasTinRequirement: false,
  showRibCard: false,
  showSwapSummaryMoreInfo: false,
  emphasisOnAfterTradeInPrice: true,
  requiresNewPriceForTradeInDiscount: true,
  tradeInProvider: null,
}

export function useBuybackConfig(market?: MarketCountryCode): BuybackData {
  const config = market
    ? buybackConfig[market]
    : buybackConfig[useMarketplace().market.countryCode]

  return config ?? fallbackBuybackConfig
}
